import { ArrowDropDown, ArrowDropUp, Close, FilterList, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Theme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
import useCollapse from 'react-collapsed';
import { SearchSection, SMBSelect } from '../../../components';
import {
  IDropdownResponse,
  IOrderType,
  IPlaceOrderChecklist,
  IPlaceOrderChecklistJob,
  IPlaceOrderChecklistTerritory,
  IVendor
} from '../../../models';

interface IChecklistFilters {
  searchedValue: string;
  setSearchedValue: (val: string) => void;
  territories: IPlaceOrderChecklistTerritory[];
  isLoadingTerritories: boolean;
  jobs: IPlaceOrderChecklistJob[];
  isLoadingJobs: boolean;
  vendors: IVendor[];
  isLoadingVendors: boolean;
  types: IOrderType[];
  isLoadingTypes: boolean;
  statuses: IDropdownResponse[];
  isLoadingStatuses: boolean;
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  hasAppliedFilters?: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  selectedTerritory: IPlaceOrderChecklistTerritory | null;
  setSelectedTerritory: (val: IPlaceOrderChecklistTerritory | null) => void;
  selectedJob: IPlaceOrderChecklistJob | null;
  setSelectedJob: (val: IPlaceOrderChecklistJob | null) => void;
  selectedVendor: IVendor | null;
  setSelectedVendor: (val: IVendor | null) => void;
  selectedType: IOrderType | null;
  setSelectedType: (val: IOrderType | null) => void;
  selectedStatus: IDropdownResponse | null;
  setSelectedStatus: (val: IDropdownResponse | null) => void;
  fetchChecklists: () => void;
  setChecklists: (val: IPlaceOrderChecklist[] | null) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
}

export const PendingChecklistFilters: FC<IChecklistFilters> = ({
  searchedValue,
  setSearchedValue,
  territories,
  isLoadingTerritories,
  jobs,
  isLoadingJobs,
  vendors,
  isLoadingVendors,
  types,
  isLoadingTypes,
  statuses,
  isLoadingStatuses,
  isLoading,
  applyFilters,
  hasAppliedFilters,
  setHasAppliedFilters,
  selectedTerritory,
  setSelectedTerritory,
  selectedJob,
  setSelectedJob,
  selectedVendor,
  setSelectedVendor,
  selectedType,
  setSelectedType,
  selectedStatus,
  setSelectedStatus,
  fetchChecklists,
  setChecklists,
  setSearchValue,
  searchValue
}) => {
  const classes = useStyles();
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true
  });

  const handleDisableButton = () => {
    if (
      isLoading ||
      isLoadingTerritories ||
      isLoadingJobs ||
      isLoadingVendors ||
      isLoadingTypes ||
      isLoadingStatuses ||
      !selectedTerritory ||
      !selectedJob
    )
      return true;
    return false;
  };

  // Used to help determine when to toggle hasAppliedFilters on/off for search specific functions
  const hasSelectedFilters =
    !!selectedTerritory || !!selectedJob || !!selectedVendor || !!selectedType || !!selectedStatus;

  return (
    <>
      <Box
        display="flex"
        width={{
          xs: '100%',
          sm: 'auto'
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          className={classes.mobileButton}
          startIcon={<FilterList />}
          endIcon={isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
          disabled={isLoading}
          {...getToggleProps()}
        >
          Filters
        </Button>
      </Box>
      <Box {...getCollapseProps()}>
        <Grid container alignItems="center" spacing={1} marginTop={theme => theme.spacing(0.5)}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <SMBSelect
              variant="outlined"
              label={isLoadingTerritories ? 'Loading...' : 'Territory'}
              value={selectedTerritory ? selectedTerritory.territoryId : ''}
              onChange={e => {
                const selectedTerritory = territories.find(
                  territory => Number(e.target.value) === territory.territoryId
                );
                if (selectedTerritory) {
                  setSelectedTerritory(selectedTerritory);
                  setSelectedJob(undefined);
                  setChecklists([]);
                }
              }}
              disabled={isLoadingTerritories || isLoading}
              handleClear={() => {
                setSelectedTerritory(null);
                setSelectedJob(null);
              }}
            >
              {territories &&
                !isLoadingTerritories &&
                territories.map((territory, index) => {
                  return (
                    <MenuItem
                      key={`${territory.territoryId}-${index}`}
                      value={territory.territoryId}
                    >
                      {territory.name}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <SMBSelect
              variant="outlined"
              label={isLoadingJobs ? 'Loading...' : 'Jobs'}
              disabled={!jobs || isLoadingJobs || !selectedTerritory || isLoading}
              value={selectedJob ? selectedJob.description : ''}
              onChange={e => {
                setSelectedJob(jobs.filter(job => e.target.value === job.description)[0]);
              }}
              handleClear={() => {
                setSelectedJob(null);
              }}
            >
              {!isLoadingJobs && jobs?.length === 0 && (
                <MenuItem disabled>No Jobs available</MenuItem>
              )}
              {jobs &&
                !isLoadingJobs &&
                jobs.map((job, index) => {
                  return (
                    <MenuItem key={`${job.jobSageId}-${index}`} value={job.description}>
                      {job.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          {!!selectedTerritory && !!selectedJob && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  placeholder="Search..."
                  title="Search by order number..."
                  name="search"
                  value={searchValue}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        className={classes.searchIcon}
                        onClick={() => {
                          if (searchValue.length > 0) {
                            setHasAppliedFilters(true);
                          } else if (!hasSelectedFilters) {
                            setHasAppliedFilters(false);
                          }
                          setSearchedValue(searchValue);
                          applyFilters();
                        }}
                      >
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue ? (
                      <InputAdornment
                        position="end"
                        className={classes.searchIcon}
                        onClick={() => {
                          setSearchValue('');
                        }}
                      >
                        <Close />
                      </InputAdornment>
                    ) : null
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      if (searchValue.length > 0) {
                        setHasAppliedFilters(true);
                      } else if (!hasSelectedFilters) {
                        setHasAppliedFilters(false);
                      }
                      setSearchedValue(searchValue);
                      applyFilters();
                    }
                  }}
                  onChange={e => {
                    setSearchValue(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    value={selectedVendor}
                    classes={{
                      paper: classes.paperBorder
                    }}
                    onChange={(event, newValue: IVendor) => {
                      setSelectedVendor(newValue);
                    }}
                    id="vendors"
                    options={vendors || []}
                    disabled={isLoadingVendors || isLoading}
                    getOptionLabel={(option: IVendor) => {
                      // Regular option
                      return option.name;
                    }}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.vendorId}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        autoComplete="none"
                        label="Vendors"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <SMBSelect
                  variant="outlined"
                  label={isLoadingTerritories ? 'Loading...' : 'Type'}
                  disabled={isLoadingTypes || isLoading || !types}
                  value={selectedType ? selectedType.sageId : ''}
                  onChange={e => {
                    const selectedType = types.find(type => Number(e.target.value) === type.sageId);
                    if (selectedType) {
                      setSelectedType(selectedType);
                    }
                  }}
                  handleClear={() => {
                    setSelectedType(null);
                  }}
                >
                  {types &&
                    !isLoadingTypes &&
                    types.map((type, index) => {
                      return (
                        <MenuItem key={`${type.sageId}-${index}`} value={type.sageId}>
                          {type.typeName}
                        </MenuItem>
                      );
                    })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <SMBSelect
                  variant="outlined"
                  label={isLoadingStatuses ? 'Loading...' : 'Status'}
                  disabled={isLoadingStatuses || isLoading || !statuses}
                  value={selectedStatus ? selectedStatus.value : ''}
                  onChange={e => {
                    const selected = statuses.find(status => e.target.value === status.value);
                    if (selected) {
                      setSelectedStatus(selected);
                    }
                  }}
                  handleClear={() => {
                    setSelectedStatus(null);
                  }}
                >
                  {statuses &&
                    !isLoadingStatuses &&
                    statuses.map((status, index) => {
                      return (
                        <MenuItem key={`${status.value}-${index}`} value={status.value}>
                          {status.description}
                        </MenuItem>
                      );
                    })}
                </SMBSelect>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              disabled={handleDisableButton()}
              startIcon={<FilterList />}
              onClick={() => {
                if (searchValue.length > 0 || hasSelectedFilters) {
                  setHasAppliedFilters(true);
                } else {
                  setHasAppliedFilters(false);
                }
                if (searchValue.length > 0) {
                  setSearchedValue(searchValue);
                }
                applyFilters();
              }}
            >
              Apply Filters
            </Button>
            {hasAppliedFilters && (
              <Button
                color="inherit"
                className={clsx(classes.button, classes.resetButton)}
                variant="contained"
                disabled={isLoading}
                startIcon={<Close />}
                onClick={() => {
                  setHasAppliedFilters(false);
                  setSearchValue('');
                  setSearchedValue('');
                  setSelectedTerritory(null);
                  setSelectedJob(null);
                  setSelectedVendor(null);
                  setSelectedType(null);
                  setSelectedStatus(null);
                  applyFilters(true);
                }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      {searchedValue && (
        <Box marginTop={theme => theme.spacing(1)}>
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearchValue('');
              setSearchedValue('');
              if (!hasSelectedFilters) {
                setHasAppliedFilters(false);
              }
              applyFilters();
            }}
          />
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    marginTop: '-15px',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  divider: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
